import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useEditor, EditorContent, NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';
import { Extension, Node as TiptapNode, mergeAttributes } from '@tiptap/core';
import { Selection } from '@tiptap/pm/state';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import { TextStyle } from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import FontFamily from '@tiptap/extension-font-family';
import TextAlign from '@tiptap/extension-text-align';
import Subscript from '@tiptap/extension-subscript';
import Superscript from '@tiptap/extension-superscript';
import { Maximize2, Minimize2 } from 'lucide-react';
import {
  Table,
  TableRow,
  TableCell,
  TableHeader,
} from '@tiptap/extension-table';
import { toHtml } from './RichContent';
import {                                   // NEW — the shared helper file you already built
  MAX_EMBED_CHARS,
  decodeCode,
  encodeCode,
  isHtmlFile,
  looksLikeHtmlCode,
  buildSrcDoc,
  buildStandaloneHtm,
  downloadFile,
} from './htmlEmbed';                      // ← change the path if your helper file has another name
import './RichText.css';

export { isRichEmpty, cleanHtml } from './RichContent';

/* ── Font size + highlight colour, stored on the same "textStyle" mark as colour/font.
      Word writes these as <span style="font-size:12pt; background:yellow">. ───────── */
const StyleExtras = Extension.create({
  name: 'styleExtras',
  addGlobalAttributes() {
    return [{
      types: ['textStyle'],
      attributes: {
        fontSize: {
          default: null,
          parseHTML: el => el.style.fontSize || null,
          renderHTML: a => (a.fontSize ? { style: `font-size: ${a.fontSize}` } : {}),
        },
        backgroundColor: {
          default: null,
          parseHTML: el => el.style.backgroundColor || null,
          renderHTML: a => (a.backgroundColor ? { style: `background-color: ${a.backgroundColor}` } : {}),
        },
      },
    }];
  },
  addCommands() {
    const set   = attr => val => ({ chain }) => chain().setMark('textStyle', { [attr]: val }).run();
    const unset = attr => () => ({ chain }) => chain().setMark('textStyle', { [attr]: null }).removeEmptyTextStyle().run();
    return {
      setFontSize: set('fontSize'), unsetFontSize: unset('fontSize'),
      setBackgroundColor: set('backgroundColor'), unsetBackgroundColor: unset('backgroundColor'),
    };
  },
});

/* ── Table cells keep Word's shading and column widths ────────────────────────────── */
const cellWidth = (el) => {
  const dw = el.getAttribute('data-colwidth');
  if (dw) return dw.split(',').map(n => parseInt(n, 10));
  if (el.colSpan > 1) return null;
  const w = el.style.width || el.getAttribute('width') || '';
  const n = parseFloat(w);
  if (!n || /%$/.test(w)) return null;
  return [Math.round(/pt$/.test(w) ? n * 1.333 : n)];
};
const cellAttrs = (parent = {}) => ({
  ...parent,
  colwidth: { ...parent.colwidth, parseHTML: cellWidth },
  backgroundColor: {
    default: null,
    parseHTML: el => el.style.backgroundColor || el.getAttribute('bgcolor') || null,
    renderHTML: a => (a.backgroundColor ? { style: `background-color: ${a.backgroundColor}` } : {}),
  },
});
const Cell   = TableCell.extend({   addAttributes() { return cellAttrs(this.parent?.()); } });
const Header = TableHeader.extend({ addAttributes() { return cellAttrs(this.parent?.()); } });

/* ── Images: inline (so the Align buttons can centre them), base64 allowed (that is how
      pasted SVGs are embedded), plus a width attribute for the S / M / L size buttons. ── */
const Img = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      width: {
        default: null,
        parseHTML: el => {
          const w = el.style.width || el.getAttribute('width');
          return w ? (/^\d+$/.test(w) ? `${w}px` : w) : null;
        },
        renderHTML: a => (a.width ? { style: `width: ${a.width}; height: auto` } : {}),
      },
    };
  },
}).configure({ inline: true, allowBase64: true });

/* ═════════════════════════════════════════════════════════════════════════════════════
   NEW — HTML / SVG animation embed
   Pasted code is stored as   <div data-html-embed data-code="BASE64">   (see htmlEmbed.js)
   and shown inside the editor as a live, sandboxed iframe with Replay / Edit / Download.
   ═════════════════════════════════════════════════════════════════════════════════════ */

const barBtn = {
  font: '12px system-ui, sans-serif',
  padding: '2px 8px',
  cursor: 'pointer',
  border: '1px solid rgba(127,127,127,.45)',
  borderRadius: 4,
  background: 'transparent',
  color: 'inherit',
};

/** The live preview that appears in the editor in place of the pasted code. */
function HtmlEmbedView({ node, updateAttributes, deleteNode, selected }) {
  const code = node.attrs.code || '';
  const frameRef = useRef(null);
  const [height, setHeight] = useState(240);
  const [run, setRun] = useState(0);                 // bump to restart the animation
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(code);
  const srcDoc = useMemo(() => buildSrcDoc(code), [code]);

  // The iframe reports its content height so it always fits exactly
  useEffect(() => {
    const onMsg = (e) => {
      const h = e.data && e.data.htmlEmbedHeight;
      if (typeof h !== 'number' || e.source !== frameRef.current?.contentWindow) return;
      setHeight(Math.max(40, h));
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, []);

  const applyEdit = () => {
    const next = draft.trim();
    if (!next) return;
    if (next.length > MAX_EMBED_CHARS) {
      window.alert(`That code is too large (limit about ${Math.round(MAX_EMBED_CHARS / 1000)} KB).`);
      return;
    }
    updateAttributes({ code: next });
    setEditing(false);
  };

  return (
    <NodeViewWrapper
      style={{
        margin: '12px 0',
        border: `1px solid ${selected ? '#378ADD' : 'rgba(127,127,127,.35)'}`,
        borderRadius: 8,
        overflow: 'hidden',
      }}
    >
      <div
        contentEditable={false}
        style={{
          display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap',
          padding: '4px 8px', background: 'rgba(127,127,127,.12)',
          font: '12px system-ui, sans-serif',
        }}
      >
        <span data-drag-handle title="Drag to move" style={{ cursor: 'grab', marginRight: 'auto' }}>⠿ Animation</span>
        <button type="button" style={barBtn} onClick={() => setRun(r => r + 1)}>↻ Replay</button>
        <button type="button" style={barBtn} onClick={() => { setDraft(code); setEditing(e => !e); }}>
          {editing ? 'Close code' : '</> Code'}
        </button>
        <button type="button" style={barBtn} onClick={() => downloadFile('animation.htm', buildStandaloneHtm(code))}>
          ⬇ Download .htm
        </button>
        <button type="button" style={barBtn} onClick={deleteNode}>🗑</button>
      </div>

      <iframe
        key={run}
        ref={frameRef}
        title="Embedded animation"
        sandbox="allow-scripts allow-popups"          /* no allow-same-origin: the code cannot touch your site */
        srcDoc={srcDoc}
        style={{ width: '100%', height, border: 0, display: 'block' }}
      />

      {editing && (
        <div contentEditable={false} style={{ padding: 8, borderTop: '1px solid rgba(127,127,127,.35)' }}>
          <textarea
            value={draft}
            onChange={e => setDraft(e.target.value)}
            rows={10}
            spellCheck={false}
            style={{
              width: '100%', boxSizing: 'border-box',
              font: '12px ui-monospace, Menlo, Consolas, monospace',
            }}
          />
          <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
            <button type="button" style={barBtn} onClick={applyEdit}>Apply</button>
            <button type="button" style={barBtn} onClick={() => setEditing(false)}>Cancel</button>
          </div>
        </div>
      )}
    </NodeViewWrapper>
  );
}

const HtmlEmbed = TiptapNode.create({
  name: 'htmlEmbed',
  group: 'block',
  atom: true,
  draggable: true,
  selectable: true,

  addAttributes() {
    return {
      code: {
        default: '',
        parseHTML: el => decodeCode(el.getAttribute('data-code') || ''),
        renderHTML: a => (a.code ? { 'data-code': encodeCode(a.code) } : {}),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-html-embed]', priority: 100 }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes({ 'data-html-embed': '' }, HTMLAttributes)];
  },

  addNodeView() {
    return ReactNodeViewRenderer(HtmlEmbedView);
  },
});

/** ```html … ```  ->  the code inside the fence (people often copy the whole code block) */
const unfence = (t) => {
  const m = (t || '').trim().match(/^```[a-z0-9]*[ \t]*\r?\n([\s\S]*?)\r?\n?```$/i);
  return (m ? m[1] : (t || '')).trim();
};

const tooLarge = () =>
  window.alert(`That code is too large (limit about ${Math.round(MAX_EMBED_CHARS / 1000)} KB).`);

/** Put an animation at the cursor (or at `pos`) and keep a paragraph after it so you can keep typing. */
function insertEmbed(editor, code, pos) {
  if (!editor || !code) return;
  if (code.length > MAX_EMBED_CHARS) { tooLarge(); return; }

  const chain = editor.chain().focus();
  if (pos != null) chain.setTextSelection(pos);
  chain.insertContent({ type: 'htmlEmbed', attrs: { code } }).run();

  const { doc } = editor.state;
  if (doc.lastChild?.type.name === 'htmlEmbed') {
    editor.chain().insertContentAt(doc.content.size, { type: 'paragraph' }).run();
  }
}

/** .html / .htm files -> one animation each */
async function insertHtmlFiles(editor, files, pos) {
  let at = pos;
  for (const file of files) {
    try {
      const text = await readFile(file, 'text');
      insertEmbed(editor, unfence(text), at);
      at = undefined;                                  // later files go after the first
    } catch (err) {
      window.alert(err.message || 'Could not read that file.');
    }
  }
}

/* ── Image / SVG helpers ───────────────────────────────────────────────────────────── */
const MAX_SVG_CHARS    = 1_000_000;   // ~1 MB of SVG markup
const MAX_RASTER_BYTES = 1_500_000;   // PNG / JPG / GIF / WebP files

const isImageFile = f => f.type.startsWith('image/') || /\.svg$/i.test(f.name);

const looksLikeSvg = t =>
  /^(<\?xml[^>]*>\s*)?(<!DOCTYPE[^>]*>\s*)?(<!--[\s\S]*?-->\s*)*<svg[\s>]/i.test(t) && /<\/svg>\s*$/i.test(t);

const readFile = (file, as) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(r.result);
  r.onerror = () => reject(new Error('Could not read that file.'));
  as === 'text' ? r.readAsText(file) : r.readAsDataURL(file);
});

const toBase64 = str => {
  const bytes = new TextEncoder().encode(str);
  let bin = '';
  for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode.apply(null, bytes.subarray(i, i + 0x8000));
  return btoa(bin);
};

/** SVG markup -> sanitised data: URI. Rendered through <img>, so scripts never run;
    we still strip scripts / handlers so the stored file is clean everywhere else. */
function svgToSrc(raw) {
  const doc = new DOMParser().parseFromString(raw, 'text/html');   // lenient parser; adds xmlns on serialise
  const svg = doc.body.querySelector('svg');
  if (!svg) throw new Error('That does not look like a valid SVG.');

  svg.querySelectorAll('script, foreignObject, iframe, object, embed, audio, video').forEach(n => n.remove());
  [svg, ...svg.querySelectorAll('*')].forEach(el => {
    [...el.attributes].forEach(({ name, value }) => {
      const n = name.toLowerCase();
      const v = value.trim().toLowerCase();
      const badHref = (n === 'href' || n === 'xlink:href') && /^(javascript:|data:text\/html)/.test(v);
      if (n.startsWith('on') || badHref) el.removeAttribute(name);
    });
  });

  const xml = new XMLSerializer().serializeToString(svg);
  if (xml.length > MAX_SVG_CHARS) throw new Error('That SVG is too large (limit about 1 MB).');
  return `data:image/svg+xml;base64,${toBase64(xml)}`;
}

/** item = File, or a string of raw SVG markup */
async function toSrc(item) {
  if (typeof item === 'string') return svgToSrc(item);
  if (item.type === 'image/svg+xml' || /\.svg$/i.test(item.name)) return svgToSrc(await readFile(item, 'text'));
  if (item.size > MAX_RASTER_BYTES) throw new Error(`Image is too large (limit ${Math.round(MAX_RASTER_BYTES / 1000)} KB).`);
  return readFile(item, 'dataURL');
}

/** Insert one or more images at the cursor (or at `pos`, for drag-and-drop). */
async function insertImages(view, items, pos) {
  try {
    const srcs = await Promise.all(items.map(toSrc));
    const imageNode = view.state.schema.nodes.image;
    if (!imageNode) return;
    let tr = view.state.tr;
    if (pos != null) tr = tr.setSelection(Selection.near(tr.doc.resolve(pos)));
    srcs.forEach(src => { tr = tr.replaceSelectionWith(imageNode.create({ src })); });
    view.dispatch(tr.scrollIntoView());
    view.focus();
  } catch (err) {
    window.alert(err.message || 'Could not insert that image.');
  }
}

/** What on the clipboard should become an image? Returns [] when it is ordinary rich text,
    so Word / web-page pastes (tables, bold, colours…) keep going through Tiptap as before. */
function clipboardImages(cd) {
  if (!cd) return [];

  // 1. SVG source code copied as text (Figma "Copy as SVG", Inkscape, VS Code, an .svg opened in an editor…)
  const plain = (cd.getData('text/plain') || '').trim();
  if (looksLikeSvg(plain)) return [plain];

  const files = Array.from(cd.files || []).filter(isImageFile);
  const html  = cd.getData('text/html');
  if (!html) return files;                                        // 2. image file / screenshot, nothing else

  const body = new DOMParser().parseFromString(html, 'text/html').body;
  const rest = body.cloneNode(true);
  rest.querySelectorAll('svg').forEach(n => n.remove());
  const bare = !rest.textContent.trim() && !rest.querySelector('table, ul, ol');   // html is just a picture

  const svgEl = body.querySelector('svg');
  if (svgEl && bare) return [svgEl.outerHTML];                    // 3. inline <svg> copied from a web page
  if (bare && files.length) return files;                         // 4. "Copy image" in a browser / Word picture
  return [];                                                      // real rich text: don't interfere
}

const FONTS = ['Arial', 'Calibri', 'Cambria', 'Courier New', 'Georgia', 'Tahoma', 'Times New Roman', 'Trebuchet MS', 'Verdana'];
const SIZES = [8, 9, 10, 11, 12, 14, 16, 18, 20, 24, 28, 32, 36];
const IMG_WIDTHS = ['25%', '50%', '75%', '100%'];
const hex = c => (/^#[0-9a-f]{6}$/i.test(c || '') ? c : '#000000');

const normalizeUrl = u => (/^(https?:|mailto:|tel:|#|\/)/i.test(u) ? u : `https://${u}`);

const Btn = ({ on, disabled, title, onClick, children }) => (
  <button
    type="button"
    className={`rt-btn ${on ? 'on' : ''}`}
    title={title}
    disabled={disabled}
    onMouseDown={e => e.preventDefault()}
    onClick={onClick}
  >
    {children}
  </button>
);

function Toolbar({ editor, fullscreen, onToggleFullscreen, fontOnly }) {
  const run   = () => editor.chain().focus();
  const ts    = editor.getAttributes('textStyle');
  const fam   = (ts.fontFamily || '').split(',')[0].replace(/['"]/g, '').trim();
  const size  = ts.fontSize ? String(parseFloat(ts.fontSize)) : '';
  const block = [1, 2, 3].find(l => editor.isActive('heading', { level: l })) || 'p';
  const inTable = editor.isActive('table');
  const onImage = editor.isActive('image');
  const fileRef = useRef(null);

  // NEW: animation panel
  const htmlFileRef = useRef(null);
  const [embedOpen, setEmbedOpen] = useState(false);
  const [embedText, setEmbedText] = useState('');

  const closeEmbedPanel = () => { setEmbedOpen(false); setEmbedText(''); };
  const insertFromPanel = () => {
    const code = unfence(embedText);
    if (!code) return;
    insertEmbed(editor, code);
    closeEmbedPanel();
  };

  if (fontOnly) {
    return (
      <div className="rt-toolbar">
        <div className="rt-group">
          <select className="rt-select" title="Font" aria-label="Font" value={FONTS.includes(fam) ? fam : ''}
            onChange={e => (e.target.value ? run().setFontFamily(e.target.value).run() : run().unsetFontFamily().run())}>
            <option value="">Font</option>
            {FONTS.map(f => <option key={f} value={f}>{f}</option>)}
          </select>
          <select className="rt-select" title="Size (pt)" aria-label="Font size" value={SIZES.includes(Number(size)) ? size : ''}
            onChange={e => (e.target.value ? run().setFontSize(`${e.target.value}pt`).run() : run().unsetFontSize().run())}>
            <option value="">Size</option>
            {SIZES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>
    );
  }

  const setLink = () => {
    const prev  = editor.getAttributes('link').href || '';
    const input = window.prompt('Link URL (leave empty to remove the link)', prev);
    if (input === null) return;
    const url = input.trim();
    if (!url) { run().extendMarkRange('link').unsetLink().run(); return; }
    const href = normalizeUrl(url);
    if (editor.state.selection.empty && !editor.isActive('link')) {
      // nothing selected: insert the URL itself as a link
      run().insertContent({ type: 'text', text: url, marks: [{ type: 'link', attrs: { href } }] }).run();
    } else {
      run().extendMarkRange('link').setLink({ href }).run();
    }
  };

  return (
    <div className="rt-toolbar">
      <button
        type="button"
        className="rt-fullscreen-toggle"
        onMouseDown={e => e.preventDefault()}
        onClick={onToggleFullscreen}
        aria-label={fullscreen ? 'Exit full screen editor' : 'Open editor in full screen'}
        aria-pressed={fullscreen}
        title={fullscreen ? 'Exit full screen (Esc)' : 'Open editor in full screen'}
      >
        {fullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
        <span>{fullscreen ? 'Exit full screen' : 'Full screen'}</span>
      </button>
      <div className="rt-group">
        <Btn title="Undo" disabled={!editor.can().undo()} onClick={() => run().undo().run()}>↶</Btn>
        <Btn title="Redo" disabled={!editor.can().redo()} onClick={() => run().redo().run()}>↷</Btn>
      </div>

      <div className="rt-group">
        <select className="rt-select" title="Style" value={block}
          onChange={e => (e.target.value === 'p' ? run().setParagraph().run() : run().setHeading({ level: Number(e.target.value) }).run())}>
          <option value="p">Normal</option>
          <option value="1">Heading 1</option>
          <option value="2">Heading 2</option>
          <option value="3">Heading 3</option>
        </select>
        <select className="rt-select" title="Font" value={FONTS.includes(fam) ? fam : ''}
          onChange={e => (e.target.value ? run().setFontFamily(e.target.value).run() : run().unsetFontFamily().run())}>
          <option value="">Font</option>
          {FONTS.map(f => <option key={f} value={f}>{f}</option>)}
        </select>
        <select className="rt-select" title="Size (pt)" value={SIZES.includes(Number(size)) ? size : ''}
          onChange={e => (e.target.value ? run().setFontSize(`${e.target.value}pt`).run() : run().unsetFontSize().run())}>
          <option value="">Size</option>
          {SIZES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <div className="rt-group">
        <Btn title="Bold"          on={editor.isActive('bold')}        onClick={() => run().toggleBold().run()}><b>B</b></Btn>
        <Btn title="Italic"        on={editor.isActive('italic')}      onClick={() => run().toggleItalic().run()}><i>I</i></Btn>
        <Btn title="Underline"     on={editor.isActive('underline')}   onClick={() => run().toggleUnderline().run()}><u>U</u></Btn>
        <Btn title="Strikethrough" on={editor.isActive('strike')}      onClick={() => run().toggleStrike().run()}><s>S</s></Btn>
        <Btn title="Subscript"     on={editor.isActive('subscript')}   onClick={() => run().toggleSubscript().run()}>x₂</Btn>
        <Btn title="Superscript"   on={editor.isActive('superscript')} onClick={() => run().toggleSuperscript().run()}>x²</Btn>
        <Btn title="Clear formatting" onClick={() => run().unsetAllMarks().run()}>Tx</Btn>
      </div>

      <div className="rt-group">
        <label className="rt-color" title="Text colour">
          <span>A</span>
          <input type="color" value={hex(ts.color)} onChange={e => run().setColor(e.target.value).run()} />
        </label>
        <label className="rt-color" title="Highlight colour">
          <span style={{ background: '#fff176', padding: '0 3px' }}>ab</span>
          <input type="color" value={hex(ts.backgroundColor || '#fff176')} onChange={e => run().setBackgroundColor(e.target.value).run()} />
        </label>
        <Btn title="Remove highlight" onClick={() => run().unsetBackgroundColor().run()}>✕</Btn>
      </div>

      <div className="rt-group">
        {[['left', '⬅'], ['center', '↔'], ['right', '➡'], ['justify', '☰']].map(([a, icon]) => (
          <Btn key={a} title={`Align ${a}`} on={editor.isActive({ textAlign: a })} onClick={() => run().setTextAlign(a).run()}>{icon}</Btn>
        ))}
      </div>

      <div className="rt-group">
        <Btn title="Bullet list"   on={editor.isActive('bulletList')}  onClick={() => run().toggleBulletList().run()}>• List</Btn>
        <Btn title="Numbered list" on={editor.isActive('orderedList')} onClick={() => run().toggleOrderedList().run()}>1. List</Btn>
        <Btn title="Quote"         on={editor.isActive('blockquote')}  onClick={() => run().toggleBlockquote().run()}>❝</Btn>
      </div>

      {/* links + images + animations */}
      <div className="rt-group">
        <Btn title="Add / edit link" on={editor.isActive('link')} onClick={setLink}>🔗 Link</Btn>
        <Btn title="Remove link" disabled={!editor.isActive('link')} onClick={() => run().extendMarkRange('link').unsetLink().run()}>Unlink</Btn>
        <Btn title="Insert image or SVG (you can also paste or drag one in)" onClick={() => fileRef.current?.click()}>🖼 Image</Btn>
        <Btn title="Insert an HTML / SVG animation (you can also just paste the code into the editor)" on={embedOpen}
          onClick={() => setEmbedOpen(o => !o)}>▶ Animation</Btn>
        <input
          ref={fileRef}
          type="file"
          accept=".svg,image/svg+xml,image/*"
          multiple
          hidden
          onChange={e => {
            const files = Array.from(e.target.files || []).filter(isImageFile);
            if (files.length) insertImages(editor.view, files);
            e.target.value = '';
          }}
        />
      </div>

      <div className="rt-group">
        <Btn title="Insert table (3 × 3)" onClick={() => run().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}>▦ Table</Btn>
      </div>

      {embedOpen && (
        <div className="rt-tablebar" style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 6, width: '100%', boxSizing: 'border-box' }}>
          <span className="rt-tablelabel">Animation</span>
          <textarea
            value={embedText}
            onChange={e => setEmbedText(e.target.value)}
            rows={7}
            spellCheck={false}
            placeholder="Paste your HTML / SVG animation code here (a fragment with <style> + <svg> + <script>, or a full HTML page)…"
            style={{ width: '100%', boxSizing: 'border-box', font: '12px ui-monospace, Menlo, Consolas, monospace' }}
          />
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <Btn title="Insert the animation at the cursor" disabled={!embedText.trim()} onClick={insertFromPanel}>▶ Insert</Btn>
            <Btn title="Pick a .html / .htm file" onClick={() => htmlFileRef.current?.click()}>📂 Open .html file</Btn>
            <Btn title="Close" onClick={closeEmbedPanel}>Cancel</Btn>
          </div>
          <input
            ref={htmlFileRef}
            type="file"
            accept=".html,.htm,text/html"
            multiple
            hidden
            onChange={e => {
              const files = Array.from(e.target.files || []).filter(isHtmlFile);
              if (files.length) { insertHtmlFiles(editor, files); closeEmbedPanel(); }
              e.target.value = '';
            }}
          />
        </div>
      )}

      {onImage && (
        <div className="rt-tablebar">
          <span className="rt-tablelabel">Image</span>
          {IMG_WIDTHS.map(w => (
            <Btn key={w} title={`Width ${w}`} on={editor.getAttributes('image').width === w}
              onClick={() => run().updateAttributes('image', { width: w }).run()}>{w}</Btn>
          ))}
          <Btn title="Original size" onClick={() => run().updateAttributes('image', { width: null }).run()}>Auto</Btn>
          <Btn title="Delete image" onClick={() => run().deleteSelection().run()}>🗑 Image</Btn>
        </div>
      )}

      {inTable && (
        <div className="rt-tablebar">
          <span className="rt-tablelabel">Table</span>
          <Btn title="Row above"     onClick={() => run().addRowBefore().run()}>+Row ↑</Btn>
          <Btn title="Row below"     onClick={() => run().addRowAfter().run()}>+Row ↓</Btn>
          <Btn title="Column left"   onClick={() => run().addColumnBefore().run()}>+Col ←</Btn>
          <Btn title="Column right"  onClick={() => run().addColumnAfter().run()}>+Col →</Btn>
          <Btn title="Delete row"    onClick={() => run().deleteRow().run()}>−Row</Btn>
          <Btn title="Delete column" onClick={() => run().deleteColumn().run()}>−Col</Btn>
          <Btn title="Merge cells"   disabled={!editor.can().mergeCells()} onClick={() => run().mergeCells().run()}>Merge</Btn>
          <Btn title="Split cell"    disabled={!editor.can().splitCell()}  onClick={() => run().splitCell().run()}>Split</Btn>
          <Btn title="Toggle header row" onClick={() => run().toggleHeaderRow().run()}>Header row</Btn>
          <label className="rt-color" title="Cell shading">
            <span>Cell</span>
            <input type="color" value={hex(editor.getAttributes('tableCell').backgroundColor || '#ffffff')}
              onChange={e => run().setCellAttribute('backgroundColor', e.target.value).run()} />
          </label>
          <Btn title="Remove cell shading" onClick={() => run().setCellAttribute('backgroundColor', null).run()}>✕</Btn>
          <Btn title="Delete table" onClick={() => run().deleteTable().run()}>🗑 Table</Btn>
        </div>
      )}
    </div>
  );
}

/**
 * Word-style editor. `value` / `onChange` carry an HTML string ('' when empty).
 * Pasting from Microsoft Word keeps bold, italics, underline, colours, fonts, sizes,
 * alignment, lists and tables (including column widths and cell shading).
 * Links and images are supported; SVGs can be pasted (file, copied image, or SVG code),
 * dragged in, or picked with the Image button, and are embedded as data: URIs.
 *
 * NEW: paste HTML / SVG animation code (or drop / pick a .html file, or use the
 * "▶ Animation" button) and it runs live in the editor, inside a sandboxed iframe.
 */
export default function RichTextEditor({ value, onChange, toolbarMode = 'full' }) {
  const last = useRef(value || '');
  const cb   = useRef(onChange);
  cb.current = onChange;
  const [fullscreen, setFullscreen] = useState(false);

  useEffect(() => {
    if (!fullscreen) return undefined;
    const html = document.documentElement;
    const body = document.body;
    const previous = [html.style.overflow, body.style.overflow];
    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';

    const onKeyDown = event => {
      if (event.key === 'Escape') setFullscreen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      html.style.overflow = previous[0];
      body.style.overflow = previous[1];
    };
  }, [fullscreen]);

  // handlePaste / handleDrop are created before the editor exists, so they reach it through this ref
  const editorRef = useRef(null);

  const editor = useEditor({
    extensions: [
      // Tiptap v3's StarterKit already bundles Link + Underline; switch them off here
      // so the explicitly configured ones below don't register twice. (Ignored on v2.)
      StarterKit.configure({ link: false, underline: false }),
      Underline, Subscript, Superscript,
      TextStyle, Color, FontFamily, StyleExtras,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Link.configure({
        openOnClick: false,          // click = place cursor; use the Link button to edit
        autolink: true,              // typing a URL + space turns it into a link
        linkOnPaste: true,           // pasting a URL over selected text links that text
        defaultProtocol: 'https',
        HTMLAttributes: { target: '_blank', rel: 'noopener noreferrer nofollow' },
      }),
      Img,
      HtmlEmbed,                     // NEW
      Table.configure({ resizable: true }),
      TableRow, Header, Cell,
    ],
    content: toHtml(value || ''),
    editorProps: {
      attributes: { class: 'rt-content', spellcheck: 'true' },

      // Paste order:
      //  1. HTML / SVG widget code  -> live animation   (NEW)
      //  2. SVG code / SVG file / screenshot / "copy image" -> embedded image
      //  3. anything else (Word, web text, tables) falls through to Tiptap's normal paste
      handlePaste: (view, event) => {
        const cd = event.clipboardData;

        const code = looksLikeHtmlCode(unfence(cd?.getData('text/plain') || ''));
        if (code && editorRef.current) {
          event.preventDefault();
          insertEmbed(editorRef.current, code);
          return true;
        }

        const items = clipboardImages(cd);
        if (!items.length) return false;
        event.preventDefault();
        insertImages(view, items);
        return true;
      },

      // Drag an image / .svg file — or a .html file (NEW) — from the desktop straight into the editor.
      handleDrop: (view, event) => {
        const dropped = Array.from(event.dataTransfer?.files || []);
        const at = view.posAtCoords({ left: event.clientX, top: event.clientY });

        const htmlFiles = dropped.filter(isHtmlFile);
        if (htmlFiles.length && editorRef.current) {
          event.preventDefault();
          insertHtmlFiles(editorRef.current, htmlFiles, at?.pos);
          return true;
        }

        const files = dropped.filter(isImageFile);
        if (!files.length) return false;
        event.preventDefault();
        insertImages(view, files, at?.pos);
        return true;
      },
    },
    onUpdate: ({ editor: ed }) => {
      const raw  = ed.getHTML();
      // a document that holds only an animation is not "empty"
      const html = ed.isEmpty && !raw.includes('data-html-embed') ? '' : raw;
      last.current = html;
      cb.current(html);
    },
  });
  editorRef.current = editor;

  // The form was reset or switched to another question from outside
  useEffect(() => {
    if (!editor || (value || '') === last.current) return;
    last.current = value || '';
    editor.commands.setContent(toHtml(value || ''), false);
  }, [value, editor]);

  if (!editor) return <div className="rt-wrap rt-loading">Loading editor…</div>;

  return (
    <div className={`rt-wrap${fullscreen ? ' rt-fullscreen' : ''}`}>
      <Toolbar
        editor={editor}
        fullscreen={fullscreen}
        fontOnly={toolbarMode === 'font-only'}
        onToggleFullscreen={() => setFullscreen(value => !value)}
      />
      <div className="rt-editor"><EditorContent editor={editor} /></div>
      <p className="rt-hint">Tip: copy from Microsoft Word and paste here — tables, bold, italics, colours, fonts and sizes are kept. You can also paste links and SVG images (or drag an .svg file in). Paste HTML / SVG animation code and it runs live, with a Download .htm button.</p>
    </div>
  );
}